import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { FileOutput, Sparkles, Loader2, Import, RefreshCw, Rocket, ArrowRight } from "lucide-react";
import { AIGenerationLoader } from "@/components/dashboard/AIGenerationLoader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useCredits } from "@/hooks/useCredits";
import { CreditCostBadge } from "@/components/CreditGate";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import SalesPageResults from "@/components/sales-page/SalesPageResults";

export interface SalesPageData {
  headline: string;
  subheadline: string;
  heroVisual: string;
  painPoints: string[];
  solutionIntro: string;
  solutionBullets: string[];
  testimonials: { name: string; role: string; quote: string }[];
  whatYouGet: { item: string; description: string }[];
  pricing: { price: string; anchor: string; valueStack: string[]; totalValue: string };
  ctaButtonText: string;
  guarantee: string;
  faqs: { question: string; answer: string }[];
  footerCta: { closingStatement: string; buttonText: string };
}

const PRODUCT_TYPES = [
  "Ebook", "Mini Course", "Full Course", "Template Pack", "Swipe File",
  "Coaching Package", "Newsletter", "Workshop", "Challenge", "Other",
];

const FRAMEWORKS = [
  { value: "pas", label: "PAS", desc: "Problem → Agitate → Solution. Best for most products." },
  { value: "aida", label: "AIDA", desc: "Attention → Interest → Desire → Action. Great for impulse buys." },
  { value: "story", label: "Story-Based", desc: "Narrative-driven. Best for personal brand products." },
  { value: "feature", label: "Feature-Heavy", desc: "Specs & deliverables first. Best for template packs & toolkits." },
];

const SalesPageBuilder = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [productTitle, setProductTitle] = useState("");
  const [productType, setProductType] = useState("");
  const [targetAudience, setTargetAudience] = useState("");
  const [keyBenefits, setKeyBenefits] = useState("");
  const [price, setPrice] = useState("");
  const [framework, setFramework] = useState("pas");
  const [result, setResult] = useState<SalesPageData | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [importProducts, setImportProducts] = useState<any[]>([]);
  const [importOpen, setImportOpen] = useState(false);
  const { consume } = useCredits();

  useEffect(() => {
    const title = searchParams.get("title");
    const type = searchParams.get("type");
    const benefits = searchParams.get("benefits");
    const audience = searchParams.get("audience");
    const priceParam = searchParams.get("price");
    if (title) setProductTitle(title);
    if (type) setProductType(type);
    if (benefits) setKeyBenefits(benefits);
    if (audience) setTargetAudience(audience);
    if (priceParam) setPrice(priceParam);
  }, [searchParams]);

  const loadImportProducts = async () => {
    if (!user) return;
    const { data } = await supabase
      .from("monetization_products")
      .select("id, title, product_type, pricing, why_it_fits, project_id, monetization_projects(niche, audience_type)")
      .order("created_at", { ascending: false })
      .limit(20);
    setImportProducts(data || []);
    setImportOpen(true);
  };

  const handleImport = (product: any) => {
    setProductTitle(product.title || "");
    setProductType(product.product_type || "");
    if (product.why_it_fits) setKeyBenefits(product.why_it_fits);
    const proj = product.monetization_projects;
    if (proj?.audience_type) setTargetAudience(proj.audience_type);
    const p = product.pricing as any;
    if (p?.suggestedPrice) setPrice(String(p.suggestedPrice));
    setImportOpen(false);
    toast.success("Product details imported!");
  };

  const handleGenerate = async () => {
    if (!productTitle.trim()) { toast.error("Please enter your product title"); return; }
    const success = await consume("sales-page-builder", "Sales Page Builder AI generation");
    if (!success) return;
    setIsGenerating(true);
    setResult(null);
    try {
      const { data, error } = await supabase.functions.invoke("monetization-ai", {
        body: {
          type: "sales-page-builder",
          params: {
            productTitle: productTitle.trim(),
            productType,
            targetAudience: targetAudience.trim(),
            keyBenefits: keyBenefits.trim(),
            price: price.trim(),
            framework,
          },
        },
      });
      if (error) throw error;
      const parsed = typeof data === "string" ? JSON.parse(data) : data;
      setResult(parsed);
      toast.success("Sales page blueprint generated!");
    } catch (err: any) {
      console.error("Sales Page Builder error:", err);
      toast.error(err.message || "Failed to generate. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRegenerateSection = async (sectionKey: string) => {
    if (!result) return;
    try {
      const { data, error } = await supabase.functions.invoke("monetization-ai", {
        body: {
          type: "sales-page-regenerate-section",
          params: {
            productTitle: productTitle.trim(),
            productType,
            targetAudience: targetAudience.trim(),
            keyBenefits: keyBenefits.trim(),
            price: price.trim(),
            framework,
            sectionKey,
            currentData: result,
          },
        },
      });
      if (error) throw error;
      const parsed = typeof data === "string" ? JSON.parse(data) : data;
      setResult(prev => prev ? { ...prev, ...parsed } : prev);
      toast.success("Section regenerated!");
    } catch (err: any) {
      toast.error("Failed to regenerate section");
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-2xl bg-accent/20 border border-accent/30 flex items-center justify-center">
          <FileOutput className="w-7 h-7 text-accent-foreground" />
        </div>
        <div>
          <h1 className="text-3xl font-display font-bold text-foreground">Sales Page Builder AI</h1>
          <p className="text-muted-foreground">Generate a conversion-optimised sales page blueprint instantly</p>
        </div>
        <CreditCostBadge action="sales-page-builder" className="ml-auto" />
      </div>

      {/* Form */}
      <Card className="bg-card/50 border-border/50">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              Product Details
            </CardTitle>
            <Dialog open={importOpen} onOpenChange={setImportOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2" onClick={loadImportProducts}>
                  <Import className="w-4 h-4" />
                  Import from Monetisation AI
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Import Product Details</DialogTitle>
                </DialogHeader>
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {importProducts.length === 0 && (
                    <p className="text-sm text-muted-foreground py-4 text-center">No products found. Create one in Monetisation AI first.</p>
                  )}
                  {importProducts.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => handleImport(p)}
                      className="w-full text-left p-3 rounded-lg border border-border/50 hover:border-primary/50 hover:bg-primary/5 transition-colors"
                    >
                      <p className="font-medium text-sm text-foreground">{p.title}</p>
                      <p className="text-xs text-muted-foreground">{p.product_type}</p>
                    </button>
                  ))}
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Product Title */}
          <div>
            <label className="text-sm font-medium text-foreground mb-1 block">Product Title *</label>
            <Input placeholder="e.g. The YouTube Monetisation Blueprint" value={productTitle} onChange={(e) => setProductTitle(e.target.value)} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Product Type Dropdown */}
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">Product Type</label>
              <Select value={productType} onValueChange={setProductType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select product type" />
                </SelectTrigger>
                <SelectContent>
                  {PRODUCT_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Price with $ prefix */}
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">Price</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-medium">$</span>
                <Input
                  type="number"
                  min={0}
                  placeholder="47"
                  className="pl-7"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">Ebooks: $17-$47 · Courses: $47-$197 · Coaching: $197-$997</p>
            </div>
          </div>

          {/* Target Audience */}
          <div>
            <label className="text-sm font-medium text-foreground mb-1 block">Target Audience</label>
            <Input
              placeholder="e.g. Fitness creators with under 5K followers who want to sell their first product"
              value={targetAudience}
              onChange={(e) => setTargetAudience(e.target.value)}
            />
            <p className="text-xs text-muted-foreground mt-1">Be specific — "Fitness creators with under 5K followers" converts better than "fitness people"</p>
          </div>

          {/* Key Benefits */}
          <div>
            <label className="text-sm font-medium text-foreground mb-1 block">Key Benefits</label>
            <Textarea
              placeholder={"List 3-5 specific benefits. Focus on outcomes, not features.\n\ne.g. 'Generate a month of content ideas in 10 minutes'\nnot 'AI-powered content engine'"}
              value={keyBenefits}
              onChange={(e) => setKeyBenefits(e.target.value)}
              rows={5}
            />
            <p className="text-xs text-muted-foreground mt-1">Focus on what the buyer achieves, not what the product contains</p>
          </div>

          {/* Framework Selector */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">Sales Page Framework</label>
            <ToggleGroup
              type="single"
              value={framework}
              onValueChange={(v) => v && setFramework(v)}
              className="flex flex-wrap justify-start gap-2"
            >
              {FRAMEWORKS.map((f) => (
                <ToggleGroupItem
                  key={f.value}
                  value={f.value}
                  className="px-3 py-2 h-auto rounded-lg border border-border/50 data-[state=on]:border-primary data-[state=on]:bg-primary/10 data-[state=on]:text-primary"
                >
                  <span className="text-sm">{f.label}</span>
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
            <p className="text-xs text-muted-foreground mt-1.5">
              {FRAMEWORKS.find((f) => f.value === framework)?.desc}
            </p>
          </div>

          <Button variant="hero" size="lg" className="w-full" onClick={handleGenerate} disabled={isGenerating || !productTitle.trim()}>
            {isGenerating ? (
              <><Loader2 className="w-5 h-5 animate-spin mr-2" />Generating…</>
            ) : (
              <><Sparkles className="w-5 h-5 mr-2" />Generate Sales Page (3 Credits)</>
            )}
          </Button>
          {isGenerating && <AIGenerationLoader tool="sales-page" />}
        </CardContent>
      </Card>

      {/* Results */}
      {result && (
        <>
          <SalesPageResults
            data={result}
            onUpdate={setResult}
            onRegenerateSection={handleRegenerateSection}
          />

          {/* Next Step: Launch Advisor */}
          <div className="glass-card p-5 border-primary/20 bg-gradient-to-r from-purple-500/5 to-primary/5">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex items-center gap-3 flex-1">
                <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                  <Rocket className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="font-semibold text-sm text-foreground">Ready to launch?</p>
                  <p className="text-xs text-muted-foreground">Get a personalised 7-day launch plan for this product</p>
                </div>
              </div>
              <Button
                variant="outline"
                className="gap-2 border-purple-500/30 hover:bg-purple-500/10"
                onClick={() => {
                  const params = new URLSearchParams();
                  if (productTitle) params.set("title", productTitle);
                  if (productType) params.set("type", productType);
                  navigate(`/app/launch-advisor?${params.toString()}`);
                }}
              >
                Get Launch Plan
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SalesPageBuilder;
